import {
  FileText,
  Loader2,
  ExternalLink,
  BookOpen,
  PlayCircle,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/cn";
import { useGuideShare } from "@/lib/api/clinicianHooks";
import { useLearningResources } from "@/lib/api/hooks";
import {
  describeMessageLink,
  safeMessageUrl,
  splitMessageContent,
  linkifyMessageMarkdown,
  type MessageLink,
  type MessagePart,
} from "./messageLinks";
export { splitMessageContent } from "./messageLinks";

function linkLabel(link: MessageLink) {
  if (link.label !== link.href) return link.label;
  if (link.kind === "guide") return "Open PDF";
  if (link.kind === "learning")
    return link.resourceKind === "guide" ? "Open guide" : "Read article";
  return link.label;
}

function PlainText({
  parts,
  inverse,
}: {
  parts: MessagePart[];
  inverse: boolean;
}) {
  return (
    <span className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
      {parts.map((part, index) =>
        part.kind !== "text" ? (
          <a
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "underline underline-offset-2 break-words [overflow-wrap:anywhere]",
              inverse ? "text-inherit" : "text-ink",
            )}
          >
            {linkLabel(part)}
          </a>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index}>{part.value}</span>
        ),
      )}
    </span>
  );
}

function MarkdownText({ text, inverse }: { text: string; inverse: boolean }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => (
          <p className="mb-8 last:mb-0 break-words [overflow-wrap:anywhere]">
            {children}
          </p>
        ),
        strong: ({ children }) => (
          <strong className="font-semibold">{children}</strong>
        ),
        ul: ({ children }) => (
          <ul className="list-disc pl-16 mb-8 last:mb-0 space-y-4">
            {children}
          </ul>
        ),
        // Keep the list's first number: "3. Ship it" stays 3, not 1.
        ol: ({ children, start }) => (
          <ol start={start} className="list-decimal pl-16 mb-8 last:mb-0 space-y-4">
            {children}
          </ol>
        ),
        li: ({ children }) => <li>{children}</li>,
        a: ({ href, children }) =>
          href && safeMessageUrl(href) ? (
            <a
              href={safeMessageUrl(href)!}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "underline underline-offset-2 break-words [overflow-wrap:anywhere]",
                inverse ? "text-inherit" : "text-ink",
              )}
            >
              {children === href
                ? linkLabel(describeMessageLink(href))
                : children}
            </a>
          ) : (
            <span>{children}</span>
          ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

export function MessageDocumentCard({
  href,
  title,
  mime,
  openHref,
}: {
  href: string;
  title: string;
  mime?: string | null;
  openHref?: string;
}) {
  const pdf = mime === "application/pdf" || /\.pdf(?:$|[?#])/i.test(href);
  const target = openHref ?? href;

  return (
    <section className="relative mt-7 min-w-0 overflow-hidden border border-line bg-raised text-ink shadow-sm group">
      {pdf && (
        <div
          className="h-96 overflow-hidden border-b border-line-subtle bg-sunken"
          aria-hidden
        >
          <iframe
            title={`${title} preview`}
            src={`${href}#page=1&toolbar=0&navpanes=0&scrollbar=0`}
            loading="lazy"
            tabIndex={-1}
            className="h-[15rem] w-full border-0 pointer-events-none origin-top"
          />
        </div>
      )}
      <div className="flex min-w-0 items-center gap-8 px-10 py-9">
        <FileText size={17} className="shrink-0 text-ink-muted" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="truncate text-12 font-medium">{title}</p>
          <p className="text-10 text-ink-muted">
            {pdf ? "PDF document" : "Open attachment"}
          </p>
        </div>
        <ExternalLink
          size={14}
          className="shrink-0 text-ink-muted"
          aria-hidden
        />
      </div>
      <a
        href={target}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink"
        aria-label={`Open ${title}`}
      />
    </section>
  );
}

function GuideAttachmentCard({ href, token }: { href: string; token: string }) {
  const guide = useGuideShare(token);

  if (guide.isLoading) {
    return (
      <div className="mt-7 flex min-w-0 items-center gap-8 border border-line bg-raised px-10 py-10 text-ink">
        <Loader2
          size={16}
          className="shrink-0 animate-spin text-ink-muted"
          aria-hidden
        />
        <span className="text-12 text-ink-muted">Loading PDF…</span>
      </div>
    );
  }

  if (!guide.data) {
    return (
      <MessageDocumentCard
        href={href}
        openHref={href}
        title="Open secure PDF"
      />
    );
  }

  return (
    <MessageDocumentCard
      href={guide.data.view_url}
      openHref={href}
      title={guide.data.title || guide.data.filename || "Patient document"}
      mime={guide.data.mime}
    />
  );
}

function LearningResourceCard({ link }: { link: MessageLink }) {
  const resources = useLearningResources();
  const resource = resources.data?.find(
    (item) => item.slug === link.slug && item.kind === link.resourceKind,
  );
  const title =
    resource?.title ||
    (link.label !== link.href ? link.label : "Portal resource");
  const Icon = link.resourceKind === "guide" ? PlayCircle : BookOpen;
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${title}`}
      className="mt-7 flex min-w-0 items-start gap-10 overflow-hidden border border-line bg-raised p-12 text-ink hover:bg-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink"
    >
      <Icon size={22} className="shrink-0 text-ink-muted" aria-hidden />
      <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
        <p className="text-10 uppercase tracking-wide text-ink-muted">
          System Labs ·{" "}
          {link.resourceKind === "guide"
            ? "Step-by-step guide"
            : "Learn article"}
        </p>
        <p className="mt-4 text-14 font-medium">{title}</p>
        {resource?.snippet && (
          <p className="mt-4 line-clamp-2 text-12 text-ink-secondary">
            {resource.snippet}
          </p>
        )}
        <p className="mt-7 text-10 text-ink-muted">View in your portal</p>
      </div>
      <ExternalLink size={14} className="shrink-0 text-ink-muted" aria-hidden />
    </a>
  );
}

export function RichMessageContent({
  text,
  markdown = false,
  inverse = false,
  literalImages = false,
}: {
  text: string;
  markdown?: boolean;
  inverse?: boolean;
  /** Plain text only: show typed image syntax as text, with its URL linked. */
  literalImages?: boolean;
}) {
  const parts = splitMessageContent(text, {
    literalImages: literalImages && !markdown,
  });
  const seen = new Set<string>();
  const previews = parts.filter((part): part is MessageLink => {
    if (part.kind !== "guide" && part.kind !== "learning") return false;
    const key =
      part.kind === "guide"
        ? `guide:${part.token}`
        : `learn:${part.resourceKind}:${part.slug}`;
    if (seen.has(key) || seen.size >= 4) return false;
    seen.add(key);
    return true;
  });
  return (
    <div className="min-w-0 max-w-full overflow-hidden">
      {markdown ? (
        <MarkdownText text={linkifyMessageMarkdown(parts)} inverse={inverse} />
      ) : (
        <PlainText parts={parts} inverse={inverse} />
      )}
      {previews.map((part) =>
        part.kind === "guide" ? (
          <GuideAttachmentCard
            key={`guide:${part.token}`}
            href={part.href}
            token={part.token!}
          />
        ) : (
          <LearningResourceCard
            key={`learn:${part.resourceKind}:${part.slug}`}
            link={part}
          />
        ),
      )}
    </div>
  );
}
